/**
 * Activity tracker for Kestrel viewer sessions.
 * Tracks user activity to measure "active" inspection time vs idle time.
 * Listens at document level because pixel streaming captures events internally.
 */

const IDLE_TIMEOUT_MS = 10_000 // 10 seconds of no activity = idle

interface ActivityTrackerState {
  isActive: boolean
  lastActivityTimestamp: number
  activeTimeAccumulator: number
  activeStartTimestamp: number | null
  idleTimeoutId: ReturnType<typeof setTimeout> | null
  isTabVisible: boolean
  cleanupFunctions: Array<() => void>
}

let state: ActivityTrackerState | null = null

function getState(): ActivityTrackerState {
  if (!state) {
    throw new Error(
      'Activity tracker not initialized. Call startActivityTracking first.'
    )
  }
  return state
}

function setActive(active: boolean): void {
  const currentState = getState()
  if (currentState.isActive === active) return

  const now = Date.now()

  if (active) {
    // Transitioning from idle to active
    currentState.activeStartTimestamp = now
  } else {
    // Transitioning from active to idle
    if (currentState.activeStartTimestamp !== null) {
      currentState.activeTimeAccumulator +=
        now - currentState.activeStartTimestamp
      currentState.activeStartTimestamp = null
    }
  }

  currentState.isActive = active
}

function resetIdleTimeout(): void {
  const currentState = getState()

  if (currentState.idleTimeoutId !== null) {
    clearTimeout(currentState.idleTimeoutId)
  }

  currentState.idleTimeoutId = setTimeout(() => {
    setActive(false)
  }, IDLE_TIMEOUT_MS)
}

function handleUserActivity(): void {
  const currentState = getState()

  // Ignore activity if tab is not visible
  if (!currentState.isTabVisible) return

  currentState.lastActivityTimestamp = Date.now()

  // If currently idle, transition to active
  if (!currentState.isActive) {
    setActive(true)
  }

  // Reset the idle timeout
  resetIdleTimeout()
}

function handleVisibilityChange(): void {
  const currentState = getState()
  const wasVisible = currentState.isTabVisible
  currentState.isTabVisible = !document.hidden

  if (!currentState.isTabVisible && wasVisible) {
    // Tab became hidden - pause active time
    setActive(false)
    if (currentState.idleTimeoutId !== null) {
      clearTimeout(currentState.idleTimeoutId)
      currentState.idleTimeoutId = null
    }
  } else if (currentState.isTabVisible && !wasVisible) {
    // Tab became visible - resume if there's recent activity
    const timeSinceLastActivity =
      Date.now() - currentState.lastActivityTimestamp
    if (timeSinceLastActivity < IDLE_TIMEOUT_MS) {
      setActive(true)
      resetIdleTimeout()
    }
  }
}

/**
 * Starts activity tracking for the Kestrel viewer.
 * Listens for user interactions at document level.
 * @returns Cleanup function to stop tracking
 */
export function startActivityTracking(): () => void {
  if (state !== null) {
    // Already tracking - clean up first
    stopActivityTracking()
  }

  state = {
    isActive: false,
    lastActivityTimestamp: Date.now(),
    activeTimeAccumulator: 0,
    activeStartTimestamp: null,
    idleTimeoutId: null,
    isTabVisible: !document.hidden,
    cleanupFunctions: [],
  }

  // Activity events to track at document level
  // Using document because pixel streaming captures events on its internal canvas/video
  const mouseEvents: Array<keyof DocumentEventMap> = [
    'mousedown',
    'mouseup',
    'mousemove',
    'click',
    'dblclick',
    'wheel',
  ]

  const keyboardEvents: Array<keyof DocumentEventMap> = ['keydown', 'keyup']

  // Add mouse event listeners at document level
  mouseEvents.forEach((eventType) => {
    const handler = () => handleUserActivity()
    document.addEventListener(eventType, handler, { capture: true })
    state!.cleanupFunctions.push(() =>
      document.removeEventListener(eventType, handler, { capture: true })
    )
  })

  // Add keyboard event listeners at document level
  keyboardEvents.forEach((eventType) => {
    const handler = () => handleUserActivity()
    document.addEventListener(eventType, handler)
    state!.cleanupFunctions.push(() =>
      document.removeEventListener(eventType, handler)
    )
  })

  // Visibility change listener
  document.addEventListener('visibilitychange', handleVisibilityChange)
  state.cleanupFunctions.push(() =>
    document.removeEventListener('visibilitychange', handleVisibilityChange)
  )

  // Start as active if tab is visible
  if (state.isTabVisible) {
    setActive(true)
    resetIdleTimeout()
  }

  return stopActivityTracking
}

/**
 * Stops activity tracking and cleans up all listeners.
 */
export function stopActivityTracking(): void {
  if (!state) return

  // Clear idle timeout
  if (state.idleTimeoutId !== null) {
    clearTimeout(state.idleTimeoutId)
  }

  // Finalize active time if currently active
  if (state.isActive && state.activeStartTimestamp !== null) {
    state.activeTimeAccumulator += Date.now() - state.activeStartTimestamp
  }

  // Run all cleanup functions
  state.cleanupFunctions.forEach((cleanup) => cleanup())

  state = null
}

/**
 * Gets the current accumulated active time in milliseconds.
 */
export function getActiveTimeMs(): number {
  if (!state) return 0

  let total = state.activeTimeAccumulator

  // Add current active period if still active
  if (state.isActive && state.activeStartTimestamp !== null) {
    total += Date.now() - state.activeStartTimestamp
  }

  return total
}

/**
 * Resets the active time accumulator. Useful when starting a new logical session.
 */
export function resetActiveTime(): void {
  if (!state) return

  state.activeTimeAccumulator = 0
  if (state.isActive) {
    state.activeStartTimestamp = Date.now()
  }
}
